import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatDateShort } from '@/lib/timeFormatters'
import { cn } from '@/lib/utils'
import type { AgentMemoryEntry, AgentMemoryEntryType } from '@/types'
import { EditMemoryDialog } from './EditMemoryDialog'
import { DeleteMemoryDialog } from './DeleteMemoryDialog'

export function MemoryTypeBadge({ type }: { type: AgentMemoryEntryType }) {
  return (
    <Badge
      variant={type === 'Fact' ? 'secondary' : 'outline'}
      className={cn(
        'mt-0.5 shrink-0 text-[10px]',
        type === 'Fact'
          ? 'text-info-foreground bg-info border-info-foreground'
          : 'text-cancelled-foreground bg-cancelled border-cancelled-foreground'
      )}
    >
      {type}
    </Badge>
  )
}

interface MemoryEntryRowProps {
  projectId: string
  entry: AgentMemoryEntry
  canManage: boolean
}

export function MemoryEntryRow({ projectId, entry, canManage }: MemoryEntryRowProps) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <div className="space-y-2 px-4 py-3 border rounded-xl bg-card" data-testid="agent-memory-entry">
      <div className="flex items-start justify-between">
        <MemoryTypeBadge type={entry.type} />
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-muted-foreground">learned {formatDateShort(entry.createdAt)}</span>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Actions for memory entry: ${entry.content.slice(0, 40)}`}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleteOpen(true)}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{entry.content}</p>
      {entry.source && (
        <p className="text-xs text-muted-foreground">
          {entry.sourceRunId ? (
            <Link
              to={`/projects/${projectId}/workflows/runs/${entry.sourceRunId}`}
              className="underline hover:text-foreground"
            >
              {entry.source === 'Feedback' ? 'from gate feedback' : 'from run'}
            </Link>
          ) : (
            entry.source === 'Feedback' ? 'from gate feedback' : 'from run'
          )}
        </p>
      )}

      {canManage && (
        <>
          <EditMemoryDialog projectId={projectId} entry={entry} open={editOpen} onOpenChange={setEditOpen} />
          <DeleteMemoryDialog projectId={projectId} entry={entry} open={deleteOpen} onOpenChange={setDeleteOpen} />
        </>
      )}
    </div>
  )
}
